import _regeneratorRuntime from 'babel-runtime/regenerator';
import _asyncToGenerator from 'babel-runtime/helpers/asyncToGenerator';

import { computed, defineComponent, onMounted, ref, getCurrentInstance } from 'vue';
import { Uploader, UploaderBtn, UploaderDrop } from '@/components/ResumableUploader/vue-simple-uploader.js';
import { v2Host } from '@/config/host';
import { getApiPath } from '@/common/api-proxy';
import { getToken } from '@/utils/auth';
import _ from 'lodash';
import Worker from '@/file.worker.js';
import { readAsArrayBuffer } from '@com/utils/file-reader';
import SparkMD5 from 'spark-md5';
import { allowResourceTypes, allowFontTypes } from '@pt/views/pt-entry/creative-assets/media/config';
import uploaderDialog from '@pt/views/pt-entry/creative-assets/media/uploader-dialog.vue';
import uploaderListDialog from '@pt/views/pt-entry/creative-assets/media/uploader-list-dialog';
import useUploaderModule from '@pt/views/pt-entry/creative-assets/media/uploader-module';
import { FileStatus } from '@pt/store/assets/uploader.type';
import { getFileExtension } from '@com/utils/file';
import { isMd5 } from '@com/utils/md5';
import { unzip } from '@com/utils/zip-file';
import { maxFontSize } from '@com/utils/file-max-size';
import { archiveTypes } from '@/config/file-types';
import { t } from '@/lang';

export default defineComponent({
  props: {
    maxSize: {
      type: Number,
      default: 500 * 1024 * 1024 // 限制 500 MB
    }
  },
  components: {
    Uploader: Uploader,
    UploaderBtn: UploaderBtn,
    UploaderDrop: UploaderDrop,
    uploaderDialog: uploaderDialog,
    uploaderListDialog: uploaderListDialog
  },
  setup: function setup(props) {
    var _this = this;

    var instance = getCurrentInstance();
    var root = instance.proxy;
    var store = root.$store;

    var _useUploaderModule = useUploaderModule(),
        isUploadingStatus = _useUploaderModule.isUploadingStatus,
        isUploadEndStatus = _useUploaderModule.isUploadEndStatus,
        fileList = _useUploaderModule.fileList,
        isUploadWaitingStatus = _useUploaderModule.isUploadWaitingStatus,
        fileExtraList = _useUploaderModule.fileExtraList;

    var uploaderBtnAttr = {
      attrs: {
        // window 平台通过 allowMediaTypes 做自定义拦截不准确
        // mac 平台对于 mkv 这种格式，无法获取元素类型，因此统一不做拦截，后置过滤
        accept: ''
      }
    };
    var uploader = ref();

    var dialogVisible = ref(false);

    var uploaderInstance = computed(function () {
      return store.state.assets.uploader.uploaderInstance;
    });

    var uploaderInstanceFileList = computed(function () {
      if (uploaderInstance.value && uploaderInstance.value.fileList) {
        return uploader.value.uploader.fileList;
      }
      return [];
    });

    // 为啥要这样操作，因为直接监听 uploaderInstance.fileList 无法触发响应 UI
    var sysnFileLists = function sysnFileLists() {
      store.commit('assets/uploader/syncFileList', uploaderInstanceFileList.value);
      store.commit('assets/uploader/checkIsSameName');
    };

    var statusText = {
      success: '上传成功',
      error: '上传出错',
      uploading: '上传中',
      paused: '未上传',
      waiting: '等待中'
    };

    function isSupportWorker() {
      return window.Worker && _.isFunction(window.Worker);
    }

    var worker = ref(null);

    var innerOptions = {
      target: '' + v2Host + getApiPath('/upload/file'),
      chunkSize: 6 * 1024 * 1024, // 分块大小，最小是 5M，S3 的限制
      maxChunkRetries: 3, // 最大自动失败重试上传次数
      testChunks: true, // 是否开启服务器分片校验
      testMethod: 'POST', // 测试的时候使用的 HTTP 方法
      uploadMethod: 'POST', // 真正上传的时候使用的 HTTP 方法
      query: {
        bucket: 'mws-files',
        mod: 'assets'
      },
      headers: {
        'Auth-Token': getToken()
      },
      // 服务器分片校验函数，秒传及断点续传基础
      checkChunkUploadedByResponse: function checkChunkUploadedByResponse(chunk, response) {
        var res = {};

        var showErrorMessage = function showErrorMessage(msg) {
          root.$message({
            message: msg || '接口发生错误，请稍后再试或联系开发者',
            type: 'error'
          });
        };

        try {
          res = JSON.parse(response);
        } catch (e) {
          showErrorMessage();
          throw new Error(e.message || 'JSON.parse 发生错误，接口返回数据不是合法的 JSON');
        }

        console.log('checkChunkUploadedByResponse: ', res);

        var code = Number(res.code);

        if (code !== 0) {
          showErrorMessage(res.msg);
          // 这里抛出错误是为了让 vue-simple-uploader 认为这里发生了错误
          // 直接返回 false 或不返回，组件会继续执行代码（如只有一个分块的时候，会认为这个已经上传成功了，触发成功回调函数更新UI为成功状态），导致 UI 上展示不正确
          throw new Error(res.message || '抛出一个错误');
        }

        // 上传完成
        if (code === 0 && _.get(res, 'data.file.is_completed')) {
          return true;
        }

        // res.upload_chunks = [2, 3, 4, 5, 6, 8, 10, 11, 12, 13, 17, 20, 21]
        // 检查分块是否已经上传过了
        var uploadChunks = _.get(res, 'data.file.upload_chunks') || [];
        console.log('检查分块是否已经上传过了: ', uploadChunks.indexOf(chunk.offset + 1) >= 0);
        return uploadChunks.indexOf(chunk.offset + 1) >= 0;
      },
      processParams: function processParams(params) {
        // 将属性名改成 snakeCase，兼容后端接口的数据规范
        return _.mapKeys(params, function (value, key) {
          return _.snakeCase(key);
        });
      }
    };

    var validateFileSize = function validateFileSize(file) {
      var size = '';

      var type = getFileExtension(file.name);
      // 字体文件上传不超过10M
      if (allowFontTypes.includes(type.toLowerCase()) && file.size > maxFontSize * 1024 * 1024) {
        setTimeout(function () {
          root.$message.error(t('playTurbo.assetLibrary.asset.upload.errorFileSize', {
            size: maxFontSize + 'M'
          }));
        }, 100);
        return false;
      }

      var isGreaterThan1G = function isGreaterThan1G() {
        return props.maxSize > 1024 * 1024 * 1024;
      };

      if (isGreaterThan1G()) {
        size = props.maxSize / 1024 / 1024 / 1024;
        size = size + 'G';
      } else {
        size = props.maxSize / 1024 / 1024;
        size = size + 'M';
      }

      if (file.size > props.maxSize) {
        setTimeout(function () {
          root.$message.error(t('playTurbo.assetLibrary.asset.upload.errorFileSize', { size: size }));
        }, 100);
        return false;
      }
      return true;
    };

    var validateFileType = function validateFileType(file) {
      var type = getFileExtension(file.name);
      if (allowResourceTypes.includes(type.toLowerCase())) {
        return true;
      }
      root.$message.error(t('playTurbo.assetLibrary.asset.upload.errorType', { type: type }));
      return false;
    };
    var validateFile = function validateFile(file) {
      return validateFileSize(file) && validateFileType(file);
    };
    var updateStateAfterComputedMD5 = function updateStateAfterComputedMD5(file, md5) {
      // 避免赋值 md5 到同名元素，获取在 name 和 size 一致之后，并且没有 md5 的项
      var fileItem = fileList.value.find(function (item) {
        return item.name === file.name && item.size === file.size && !isMd5(item.uniqueIdentifier);
      });
      var fileExtraProp = fileExtraList.value.find(function (item) {
        return item.name === file.name && item.size === file.size && !isMd5(item.md5);
      });
      if (fileItem) {
        fileItem.uniqueIdentifier = md5;
        fileExtraProp.md5 = md5;
        store.dispatch('assets/uploader/checkAssetsIsValid', fileItem.id);
      } else {
        console.error('上传的资源列表不含该资源');
      }
    };
    var computeMD5 = function () {
      var _ref = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(file) {
        var time, buffer, md5;
        return _regeneratorRuntime.wrap(function _callee$(_context) {
          while (1) {
            switch (_context.prev = _context.next) {
              case 0:
                time = new Date().getTime();
                _context.prev = 1;
                _context.next = 4;
                return readAsArrayBuffer(file);

              case 4:
                buffer = _context.sent;
                md5 = SparkMD5.ArrayBuffer.hash(buffer);


                updateStateAfterComputedMD5(file, md5);
                console.log('MD5\u8BA1\u7B97\u5B8C\u6BD5\uFF1A' + file.name + ' MD5\uFF1A' + md5 + ' \u7528\u65F6\uFF1A' + (new Date().getTime() - time) + ' ms');
                _context.next = 14;
                break;

              case 10:
                _context.prev = 10;
                _context.t0 = _context['catch'](1);

                console.error(_context.t0);
                root.$message.error(_context.t0.message || '文件处理失败');

              case 14:
              case 'end':
                return _context.stop();
            }
          }
        }, _callee, _this, [[1, 10]]);
      }));

      return function computeMD5(_x) {
        return _ref.apply(this, arguments);
      };
    }();
    var handleCheckFile = function () {
      var _ref2 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee2(file) {
        var resZip;
        return _regeneratorRuntime.wrap(function _callee2$(_context2) {
          while (1) {
            switch (_context2.prev = _context2.next) {
              case 0:
                if (!(archiveTypes.zip.mediaTypes.indexOf(file.fileType) !== -1)) {
                  _context2.next = 7;
                  break;
                }

                _context2.next = 3;
                return unzip(file);

              case 3:
                resZip = _context2.sent;

                if (resZip.valid) {
                  _context2.next = 7;
                  break;
                }

                store.commit('assets/uploader/setCheckFailByOnline', {
                  id: file.id,
                  errorMsg: resZip.message,
                  status: FileStatus.Error
                });
                return _context2.abrupt('return');

              case 7:
                if (isSupportWorker() && worker.value) {
                  worker.value.postMessage({
                    type: 'calculate-md5',
                    file: file.file
                  }); // 派发事件给 web woker
                } else {
                  computeMD5(file.file);
                }

              case 8:
              case 'end':
                return _context2.stop();
            }
          }
        }, _callee2, _this);
      }));

      return function handleCheckFile(_x2) {
        return _ref2.apply(this, arguments);
      };
    }();
    var handleFillAdded = function handleFillAdded(file) {
      if (validateFile(file)) {
        handleCheckFile(file);
        sysnFileLists();
      } else {
        file.ignored = true;
      }
    };

    var handleWorkerMessage = function handleWorkerMessage(event) {
      if (event.data.error) {
        _this.$message.error(event.data.error.message || 'Web Worker 处理文件发生错误，请稍后再试或联系开发者');
        return;
      }

      if (event.data.type === 'calculate-md5' && event.data.md5 && event.data.file) {
        updateStateAfterComputedMD5(event.data.file, event.data.md5);
      }
    };
    // 一个文件上传成功事件，第一个参数 rootFile 就是成功上传的文件所属的根 Uploader.File 对象，它应该包含或者等于成功上传文件；第二个参数 file 就是当前成功的
    var handleFileSuccess = function handleFileSuccess(rootFile, file, response, chunk) {
      var res = {};
      try {
        res = JSON.parse(response);
      } catch (error) {
        res = {};
      }

      var code = Number(res.code);
      var uploadUnCompleted = function uploadUnCompleted() {
        store.commit('assets/uploader/updateFileExtraProp', {
          id: file.id,
          data: {
            errorMsg: res.msg,
            status: FileStatus.uploadError
          }
        });
        root.$message({ message: res.msg, type: 'error' });
      };

      if (code === 0 && _.get(res, 'data.file.is_completed')) {
        store.commit('assets/uploader/updateFileExtraProp', {
          id: file.id,
          data: {
            file_url: _.get(res, 'data.file.object_key')
          }
        });
        // 上传成功之后，同步到资产库
        store.dispatch('assets/uploader/createAssets', file.id);
      } else if (code === 1) {
        uploadUnCompleted();
      }
    };
    /**
     * 创建队列结束之后，提示窗口，3 秒之后再设置状态
     * @param commit
     */
    var handleUploadComplete = function handleUploadComplete() {
      // 全部上传结束，重置上传状态
      store.dispatch('assets/uploader/handleCreateAssetsQueue');
    };
    onMounted(function () {
      worker.value = new Worker();
      worker.value.addEventListener('message', handleWorkerMessage);
      store.commit('assets/uploader/setUploaderInstance', uploader.value.uploader);
    });
    return {
      uploader: uploader,
      innerOptions: innerOptions,
      uploaderBtnAttr: uploaderBtnAttr,
      statusText: statusText,
      handleChange: function handleChange(file) {
        console.log(file);
      },

      handleFillAdded: handleFillAdded,
      handleFileProgress: function handleFileProgress(rootFile, file, chunk) {
        store.commit('assets/uploader/updateFileExtraProp', {
          id: file.id,
          data: {
            progress: Math.round(file.progress() * 95) // 因为上传完之后还需要上传到资产库，因此进度需要乘以一个系数
          }
        });
        console.log('上传进度', file.id, file.progress());
        console.log('\u4E0A\u4F20\u4E2D ' + file.name + '\uFF0Cchunk\uFF1A' + chunk.startByte / 1024 / 1024 + ' ~ ' + chunk.endByte / 1024 / 1024);
      },

      handleFileSuccess: handleFileSuccess,
      handleFileRetry: function handleFileRetry(rootFile, file, chunk) {
        console.log('handleFileRetry', rootFile, file, chunk);
      },
      handleFileRemoved: function handleFileRemoved() {
        console.log('handleFileRemoved');
      },
      handleUploadStart: function handleUploadStart() {
        console.log('handleUploadStart');
      },

      handleUploadComplete: handleUploadComplete,
      fileList: fileList,
      uploaderInstance: uploaderInstance,
      dialogVisible: dialogVisible,
      isUploadingStatus: isUploadingStatus,
      isUploadEndStatus: isUploadEndStatus,
      isUploadWaitingStatus: isUploadWaitingStatus
    };
  }
});